import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { StatusIcon } from "app/components/Icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "app/components/Popover";
import TicketTypeIcon from "app/components/TicketTypeIcon";
import CustomTooltip from "app/components/Tooltip";
import { getPriorityByIds } from "app/containers/Features/containers/Boards/helpers";
import { selectWorkSpaceDetails } from "app/redux/selector";
import closeIconRed from "assets/svgs/close-icon-red.svg";
import link from "assets/svgs/link.svg";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { MentionType } from "../..";
import ActionButton from "./components/ActionButton";
import TicketMentionNode from "./components/TicketMentionNode";
import UserMentionNode from "./components/UserMentionNode";

const MentionNodeView = ({ node }: NodeViewProps) => {
  // Hooks
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Global State
  const workspaceDetails = useSelector(selectWorkSpaceDetails);

  // Variables
  const {
    label: rawLabel,
    id,
    type,
    teamId,
    projectId,
    ticketId,
    ticketTitle,
    ticketTypeName,
    ticketTypeIcon,
    ticketStatusName,
    ticketStatusColor,
    ticketPriority,
  } = node.attrs;
  const label = rawLabel || id || "mention";
  const isTicket = (type as MentionType) === MentionType.TICKET;
  const ticketPriorityDetails = getPriorityByIds(ticketPriority);

  // States
  const [showPopup, setShowPopup] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const { teamName, projectName } = useMemo(() => {
    const team = workspaceDetails?.teams?.find((t) => t._id === teamId);
    const project = team?.projects?.find((p) => p._id === projectId);
    return {
      teamName: team?.teamName || "Unknown Team",
      projectName: project?.projectName || "Unknown Project",
    };
  }, [workspaceDetails, teamId, projectId]);

  // Functions
  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  };

  const handleMouseEnter = useCallback(() => {
    clearHideTimeout();
    setShowPopup(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setShowPopup(false), 150);
  }, []);

  const handleCopyLink = () => {
    const url = `${window.location.href}?tid=${id}`;
    navigator.clipboard.writeText(url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (!isTicket) {
    return <UserMentionNode label={label} type={type} />;
  }

  return (
    <Popover open={showPopup}>
      <PopoverTrigger asChild>
        <NodeViewWrapper
          as="span"
          contentEditable={false}
          data-target-type={type}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <TicketMentionNode label={label} ticketTypeIcon={ticketTypeIcon} />
        </NodeViewWrapper>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={2}
        className="w-[min(480px,90vw)] min-w-[340px] bg-white rounded-lg border border-gray-100 shadow-lg overflow-hidden !p-0"
        onMouseEnter={clearHideTimeout}
        onMouseLeave={handleMouseLeave}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center justify-between p-4 pb-[10px] border-b border-gray-100">
          <span className="text-sm text-secondary leading-none">
            {teamName} / <span className="text-primary">{projectName}</span>
          </span>
          <div className="flex items-center gap-[10px]">
            <CustomTooltip
              text={isCopied ? "Copied!" : "Copy Link"}
              position="bottom"
            >
              <ActionButton
                icon={link}
                iconName="Link Icon"
                onClick={handleCopyLink}
              />
            </CustomTooltip>
            <ActionButton
              icon={closeIconRed}
              iconName="Close Icon"
              onClick={() => setShowPopup(false)}
            />
          </div>
        </div>
        <div className="p-4 pt-[10px] flex flex-col">
          <p className="text-xs text-secondary leading-none mb-[10px]">
            Ticket ID: {ticketId}
          </p>
          <p className="text-sm font-medium leading-5 text-black mb-3">
            {ticketTitle}
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            {ticketTypeName && (
              <span className="flex items-center gap-1.5 border border-gray-200 bg-white rounded-[4px] px-2.5 py-1 text-xs font-medium">
                <TicketTypeIcon iconName={ticketTypeIcon} size={16} />
                {ticketTypeName}
              </span>
            )}
            {ticketStatusName && (
              <span
                className={`flex items-center gap-1.5 border border-gray-200 bg-white rounded-[4px] px-2.5 py-1 text-xs font-medium tracking-wide text-[${ticketStatusColor}]`}
              >
                <StatusIcon size={16} color={ticketStatusColor} />
                {ticketStatusName}
              </span>
            )}
            {ticketPriority && (
              <span
                className="flex items-center gap-1.5 border border-gray-200 bg-white rounded px-2.5 py-1 text-xs font-medium"
                style={{ color: ticketPriorityDetails?.color }}
              >
                {ticketPriorityDetails?.icon}
                {ticketPriorityDetails?.label}
              </span>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MentionNodeView;
